import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import clone from 'lodash/clone';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Base from '../Root';
import Visit from '../../../Account/Profile/Home/Tiles/Visit';
import Header from './components/Header';
import Loading from '../../../SolvPatternLibrary/Loading';
import EmptyState from './components/EmptyState';
import useSetTitle from '../../../../hooks/useSetTitle';
import useScrollToBooking from './hooks/useScrollToBooking';
import useSelectUserProfile from '../../hooks/useSelectUserProfile';
import useSelectFetchAccountAppointments from '../../hooks/useSelectFetchAccountAppointments';
import useSelectFetchMostRecentBookingLocationDetails from '../../hooks/useSelectFetchMostRecentBookingLocationDetails';
import { colors } from '../../../../constants/colors';
import { fontFamily, fontSize } from '../../../../constants/text';
import { biggerThanOrEqualTo } from '../../../../core/util/styledComponents';
import { reorderUpcomingAppointments } from '../../util';
import * as utils from './util';
import { AccountManageTabs } from '../../constants/AccountViews';

const Root = styled(Base).withConfig({ componentId: 'sc-l8i7xy-0' })`
  min-height: 100%;
  background-color: ${colors.whiteGrey};
`;

const ContentView = styled.div.withConfig({ componentId: 'sc-l8i7xy-1' })`
  text-align: center;
  height: 100%;
  padding: 24px 0;
  box-sizing: border-box;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
`;

const TopPad = styled.div.withConfig({ componentId: 'sc-l8i7xy-2' })`
  padding-top: 5vh;
  height: 100%;
  box-sizing: border-box;
`;

const PastAppointmentYear = styled.div.withConfig({ componentId: 'sc-l8i7xy-3' })`
  margin: 18px 16px 8px;
  width: 100%;
  text-align: left;
  color: ${colors.squid};
  font-size: ${fontSize.medium};
  font-family: ${fontFamily.medium};

  ${biggerThanOrEqualTo.sm`
    text-align: center;
  `}
`;

const VisitContainer = styled.div.withConfig({ componentId: 'sc-l8i7xy-4' })`
  width: 100%;
  text-align: left;
  border-radius: 8px;
  background-color: ${colors.white};
  border: 1px solid #e6e6e6;
  box-shadow: 0 1px 8px 0 rgba(0, 0, 0, 0.1);
  box-sizing: border-box;
  max-width: 400px;
  margin: 0 16px 16px;
  height: fit-content;
`;

type Props = {
  params: any;
};

const Manage = (props: Props) => {
  useSetTitle('Manage Visits');
  const {
    params: { userProfileId },
  } = props;

  const [tabIndex, setTabIndex] = useState(AccountManageTabs.Upcoming);
  const { upcomingAppointments, pastAppointments } =
    useSelectFetchAccountAppointments(userProfileId);
  const { location: mostRecentLocation } =
    useSelectFetchMostRecentBookingLocationDetails(userProfileId);
  const locations = useSelector((state: any) => state.locations.results);
  const loading = useSelector((state: any) => !state.locations.appointmentsDataLoaded);
  useScrollToBooking(loading, pastAppointments, userProfileId, setTabIndex);

  const tileLocations = useMemo(
    () => ({
      user: locations,
      recent: mostRecentLocation,
    }),
    [locations, mostRecentLocation]
  );
  const userProfile = useSelectUserProfile(userProfileId);

  const appointments = useMemo(() => {
    if (tabIndex) {
      return clone(pastAppointments).reverse();
    }
    return reorderUpcomingAppointments(upcomingAppointments);
  }, [pastAppointments, tabIndex, upcomingAppointments]);

  const uniqueYears = new utils.UniqueYears();
  const visitState = utils.getVisitState(loading, appointments);

  const { t } = useTranslation(['account']);

  return (
    <Root>
      <Header
        firstName={userProfile.first_name}
        setTabIndex={setTabIndex}
        tabIndex={tabIndex}
        userProfileId={userProfileId}
      />

      <ContentView>
        {utils.isLoading(visitState) && (
          <TopPad>
            <Loading />
          </TopPad>
        )}
        {utils.isEmptyState(visitState) && (
          <EmptyState visitType={tabIndex ? 'past' : 'upcoming'} />
        )}
        {utils.isViewing(visitState) &&
          appointments.map((booking: any) => {
            if (!utils.getShowBooking(booking, locations)) return null;
            const bookingYear = utils.getBookingYear(booking);

            return (
              <React.Fragment key={booking.id}>
                {!!tabIndex && uniqueYears.shouldShowYear(bookingYear) && (
                  <PastAppointmentYear>
                    {t('account:manage.yearTitle', { YEAR: bookingYear })}
                  </PastAppointmentYear>
                )}

                <VisitContainer id={booking.id}>
                  <Visit
                    locations={tileLocations}
                    tile={{ booking }}
                    tileIndex={0}
                    userProfile={userProfile}
                  />
                </VisitContainer>
              </React.Fragment>
            );
          })}
      </ContentView>
    </Root>
  );
};

export default Manage;
